import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Database, X, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const MasterdataPage = () => {
  const t = useTranslation();
  const { settings } = useSettings();
  
  const [masterdata, setMasterdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', fields: [] });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Items dialog
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [selectedMasterdata, setSelectedMasterdata] = useState(null);
  const [items, setItems] = useState([]);
  const [newItemValue, setNewItemValue] = useState('');

  useEffect(() => {
    fetchMasterdata();
  }, [page]);

  const fetchMasterdata = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/masterdata`, { params: { page, page_size: 10 } });
      setMasterdata(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to fetch masterdata');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({ name: '', fields: [{ name: 'value', field_type: 'Text' }] });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ name: item.name, fields: item.fields || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.fields.length === 0) {
      toast.error('At least one field is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/masterdata/${editingId}`, formData);
        toast.success('Masterdata updated');
      } else {
        await axios.post(`${API}/masterdata`, formData);
        toast.success('Masterdata created');
      }
      setDialogOpen(false);
      fetchMasterdata();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/masterdata/${deleteId}`);
      toast.success('Masterdata deleted');
      fetchMasterdata();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const addField = () => {
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, { name: '', field_type: 'Text' }]
    }));
  };

  const updateField = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, [key]: value } : f)
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  // Items management
  const openItemsDialog = async (md) => {
    setSelectedMasterdata(md);
    setItemsDialogOpen(true);
    try {
      const res = await axios.get(`${API}/masterdata/${md.id}/items`, { params: { page_size: 100 } });
      setItems(res.data.items);
    } catch (error) {
      toast.error('Failed to fetch items');
    }
  };

  const addItem = async () => {
    if (!newItemValue.trim()) return;
    try {
      await axios.post(`${API}/masterdata/${selectedMasterdata.id}/items`, { value: newItemValue });
      const res = await axios.get(`${API}/masterdata/${selectedMasterdata.id}/items`, { params: { page_size: 100 } });
      setItems(res.data.items);
      setNewItemValue('');
      toast.success('Item added');
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/masterdata/${selectedMasterdata.id}/items/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="masterdata-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {t('masterdata')}
          </h1>
          <p className="text-gray-500 mt-1">Manage dropdown options and reference data</p>
        </div>
        <Button 
          onClick={handleCreate}
          className="gap-2"
          data-testid="create-masterdata-btn"
        >
          <Plus size={18} />
          {t('addNew')}
        </Button>
      </div>

      {/* Table */}
      <Card className="border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : masterdata.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('noResults')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-600">{t('name')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('fields')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdAt')}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {masterdata.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      {item.fields?.map(f => (
                        <span key={f.name} className="inline-block px-2 py-1 bg-gray-100 rounded text-xs mr-1">
                          {f.name} ({f.field_type})
                        </span>
                      ))}
                    </TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openItemsDialog(item)}
                          data-testid={`manage-items-${item.id}`}
                        >
                          Manage Items
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-masterdata-${item.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(item.id)}
                          data-testid={`delete-masterdata-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingId ? 'Edit Masterdata' : 'Create Masterdata'}
            </DialogTitle>
            <DialogDescription>
              Configure the masterdata structure
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Document Types"
                data-testid="masterdata-name-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('fields')}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
                  <Plus size={14} className="mr-1" />
                  Add Field
                </Button>
              </div>
              <div className="space-y-2">
                {formData.fields.map((field, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={field.name}
                      onChange={(e) => updateField(idx, 'name', e.target.value)}
                      placeholder="Field name"
                      className="flex-1"
                      data-testid={`field-name-${idx}`}
                    />
                    <Select 
                      value={field.field_type} 
                      onValueChange={(v) => updateField(idx, 'field_type', v)}
                    >
                      <SelectTrigger className="w-32" data-testid={`field-type-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Text">{t('text')}</SelectItem>
                        <SelectItem value="Number">{t('number')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => removeField(idx)}
                      data-testid={`remove-field-${idx}`}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              data-testid="save-masterdata-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Items Dialog */}
      <Dialog open={itemsDialogOpen} onOpenChange={setItemsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              Manage Items - {selectedMasterdata?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={newItemValue}
                onChange={(e) => setNewItemValue(e.target.value)}
                placeholder="Enter new item value"
                onKeyPress={(e) => e.key === 'Enter' && addItem()}
                data-testid="new-item-input"
              />
              <Button onClick={addItem} data-testid="add-item-btn">
                <Plus size={16} />
              </Button>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm">{item.values?.value || JSON.stringify(item.values)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteItem(item.id)}
                    data-testid={`delete-item-${item.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-center text-gray-500 py-4">No items yet</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this masterdata and all its items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MasterdataPage;
